import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {Navigate} from '@ngxs/router-plugin';
import {IAddress} from 'src/app/interfaces/general/profile-definitions/address.interface';
import {AddressesService} from 'src/app/modules/addresses/addresses.service';
import {AppInsightsService} from 'src/app/core/app-insights/app-insights.service';
import {errorHandler, IErrorHandlerArgs} from 'src/app/shared/helpers/error-handler';
import {
    CreateAddress,
    DeleteAddress,
    GetAddress,
    GetAddresses,
    UpdateAddress,
} from './addresses.actions';

export interface IAddressesState {
    addresses: IAddress[];
    currentAddress: IAddress | null;
    loading: boolean;
    saving: boolean;
    hasValue: boolean;
    error: any;
    saveError: any;
}

@State<IAddressesState>({
    name: 'addresses',
    defaults: {
        addresses: [],
        currentAddress: null,
        loading: false,
        saving: false,
        hasValue: false,
        error: null,
        saveError: null,
    },
})
@Injectable()
export class AddressesState {
    private readonly _errorHandlerArgsInit: IErrorHandlerArgs = {
        error: null,
        appInsightsSrv: this.insights,
        scope: 'AddressesState',
    };

    constructor(
        private addressesService: AddressesService,
        private store: Store,
        private insights: AppInsightsService,
    ) {}

    @Selector()
    public static addresses(state: IAddressesState): IAddress[] {
        return state.addresses;
    }

    @Selector()
    public static currentAddress(state: IAddressesState): IAddress | null {
        return state.currentAddress;
    }

    @Selector()
    public static loading(state: IAddressesState): boolean {
        return state.loading;
    }

    @Selector()
    public static saving(state: IAddressesState): boolean {
        return state.saving;
    }

    @Selector()
    public static hasValue(state: IAddressesState): boolean {
        return state.hasValue;
    }

    @Selector()
    public static error(state: IAddressesState): any {
        return state.error;
    }

    @Selector()
    public static saveError(state: IAddressesState): any {
        return state.saveError;
    }

    @Action(GetAddresses)
    public async getAddresses(ctx: StateContext<IAddressesState>): Promise<void> {
        ctx.patchState({loading: true, error: null});
        try {
            const addresses = await firstValueFrom(this.addressesService.getAll());
            ctx.patchState({
                addresses: addresses ?? [],
                hasValue: !!addresses?.length,
                loading: false,
            });
        } catch (error) {
            errorHandler({...this._errorHandlerArgsInit, error});
            ctx.patchState({loading: false, error});
        }
    }

    @Action(GetAddress)
    public async getAddress(
        ctx: StateContext<IAddressesState>,
        {identifier}: GetAddress,
    ): Promise<void> {
        ctx.patchState({loading: true, error: null, currentAddress: null});
        try {
            const address = await firstValueFrom(this.addressesService.getById(identifier));
            ctx.patchState({currentAddress: address, loading: false});
        } catch (error) {
            errorHandler({...this._errorHandlerArgsInit, error});
            ctx.patchState({loading: false, error});
        }
    }

    @Action(CreateAddress)
    public async createAddress(
        ctx: StateContext<IAddressesState>,
        {address}: CreateAddress,
    ): Promise<void> {
        ctx.patchState({saving: true, saveError: null});
        try {
            await firstValueFrom(this.addressesService.create(address));
            ctx.patchState({saving: false});
            this.store.dispatch(new Navigate(['/profile/addresses']));
        } catch (error) {
            errorHandler({...this._errorHandlerArgsInit, error});
            ctx.patchState({saving: false, saveError: error?.error ?? error});
        }
    }

    @Action(UpdateAddress)
    public async updateAddress(
        ctx: StateContext<IAddressesState>,
        {identifier, address}: UpdateAddress,
    ): Promise<void> {
        ctx.patchState({saving: true, saveError: null});
        try {
            const currentAddress = ctx.getState().currentAddress;
            const payload = {
                ...currentAddress,
                ...address,
                additionalType: address.additionalType ?? '',
            };
            await firstValueFrom(this.addressesService.update(identifier, payload));
            ctx.patchState({saving: false});
            this.store.dispatch(new Navigate(['/profile/addresses']));
        } catch (error) {
            errorHandler({...this._errorHandlerArgsInit, error});
            ctx.patchState({saving: false, saveError: error?.error ?? error});
        }
    }

    @Action(DeleteAddress)
    public async deleteAddress(
        ctx: StateContext<IAddressesState>,
        {identifier}: DeleteAddress,
    ): Promise<void> {
        ctx.patchState({loading: true, error: null});
        try {
            await firstValueFrom(this.addressesService.delete(identifier));
            ctx.patchState({loading: false, currentAddress: null});
            this.store.dispatch(new Navigate(['/profile/addresses']));
        } catch (error) {
            errorHandler({...this._errorHandlerArgsInit, error});
            ctx.patchState({loading: false, error});
        }
    }
}